import { Platform, ActivityIndicator } from 'react-native'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ThemedView, ThemedText, Theme } from '@app/theme'
import OTPVerification from '@sharedComponents/OTPInput/OTPInput';
import { authCASApi, fetchInitV2Api, getCredentialsForAssetFetchApi, getPledgeDataApi, triggerOtp, verifyPledgeOtp } from '../assetPledgeService';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { updateLinkedApplication } from '@app/store/slices/authSlice';
import { showErrorSheet } from '@app/store/slices/errorSlice';
import LottieView from 'lottie-react-native';
import Button from '@app/sharedComponents/Button/Button';
import { addCommasToNumber, trackEvent } from '@app/utils';
import SMSIcon from '@app/assets/svg/SMSIcon';
import { useTheme } from '@shopify/restyle';
import useIsMobileBrowser from '@app/hooks/useIsMobileBrowser';
import handleApiErr from '@app/helper/handleApiErr';
import { handleOtpReport } from '@app/screens/FetchPortfolio/utils';
import ReportOtp from '../../../../assets/Icons/ReportOtp';
import MFC_LOTTIE from '../../../assets/lotties/MFcentral.json'


const AnimationComponent = ({ showLoadingLottie, otpType, lottieCams, lottieKfin }: { showLoadingLottie: any, otpType: string | null | undefined, lottieCams: any, lottieKfin: any}) => {
    const [showLottie, setShowLottie] = useState(false);

useEffect(() => {
  const timeout = setTimeout(() => {
    setShowLottie(true);
  }, 290); 

  return () => clearTimeout(timeout);
}, []);

    const getTitle = (otpType: string) => {
        switch(otpType) {
            case 'KFIN_PLEDGE':
                return 'Pledging your portfolio with Kfintech'
            case 'MFC_PLEDGE':
                return 'Pledging your portfolio with MF Central'
            case 'CAMS_PLEDGE':
                return 'Pledging your portfolio with CAMS'  
            case 'KFIN_FETCH':
                return 'Authenticating your portfolio via Kfintech'   
            case 'MFC_FETCH':
                return 'Authenticating your portfolio via MF Central'   
            case 'CAMS_FETCH':
                return 'Authenticating your portfolio via CAMS'   
        }
    }

    const getLottie = (otpType: string) => {
        switch(otpType) {
            case 'KFIN_PLEDGE':
                return lottieKfin;
            case 'MFC_PLEDGE':
                return MFC_LOTTIE;
            case 'CAMS_PLEDGE':
                return lottieCams;
            case 'KFIN_FETCH':
                return lottieKfin;
            case 'MFC_FETCH':
                return MFC_LOTTIE;
            case 'CAMS_FETCH':
                return lottieCams;
        }
    }

    return (
        <ThemedView alignItems='center' justifyContent='center' bg='bg-surface-primary'>
            <ThemedText variant='h3s' color='text-primary' mb='2xl'>{getTitle(otpType)}</ThemedText>
            {showLottie ?
                <LottieView source={getLottie(otpType)} style={{ width: 273, height: 378 }} autoPlay={true}
                    loop={true} /> : <ThemedView height={378} justifyContent='center' alignItems='center'>
                    <ActivityIndicator size={12} color={'#1434CB'} />
                </ThemedView>}
        </ThemedView>
    )
}

const SuccessComponent = ({ successLottie, approvedLimit, handleContinue, otpType, prevOtpType }: { successLottie: any, approvedLimit: number, handleContinue: () => void, otpType: string, prevOtpType: string }) => {
    let title = '';
    let subTitle = '';

    switch (prevOtpType) {
        case 'KFIN_PLEDGE':
            title = 'KFintech funds marked successfully';
            subTitle = 'Please continue to mark your portfolio';
            break;
        case 'MFC_PLEDGE':
            title = 'MFC funds marked successfully';
            subTitle = 'Please continue to mark your portfolio';
            break;
        case 'CAMS_PLEDGE':
            title = 'CAMS funds marked successfully';
            subTitle = 'Please continue to mark your portfolio';
            break;
        case 'KFIN_FETCH':
            title = 'KFintech portfolio verified successfully';
            subTitle = 'Please continue to mark your portfolio';
            break;
        case 'MFC_FETCH':
            title = 'MFC portfolio verified successfully';
            subTitle = 'Please continue to mark your portfolio';
            break;
        case 'CAMS_FETCH':
            title = 'CAMS portfolio verified successfully';
            subTitle = 'Please continue to mark your portfolio';
            break;
        default:
            title = `₹ ${addCommasToNumber(approvedLimit)} unlocked successfully`;
            subTitle = 'Please proceed to the next step';
    }

    //Handle last terminal condition overwrite the title and subTitle
    if (otpType === null || otpType === undefined || otpType === '') {
        title = `₹ ${addCommasToNumber(approvedLimit)} unlocked successfully`;
        subTitle = 'Please proceed to the next step';
    }

    return (
        <ThemedView alignItems='center' justifyContent='center' bg='bg-surface-primary'>
            <LottieView source={successLottie} autoPlay loop style={{ width: 70, height: 70 }} />
            <ThemedView alignItems='center' justifyContent='center' bg='bg-surface-primary' mb='2xl'>
                <ThemedText variant='h5s' mb='sm' color='text-primary'>{title}</ThemedText>
                <ThemedText textAlign='center' variant='b3r' color='text-secondary'>{subTitle}</ThemedText>
            </ThemedView>
            <Button size='MD' label='Continue' handlePress={handleContinue} bg='#1434CB' textColor='text-primary' />
        </ThemedView>
    )
}


const KarvyPledgeOtpModal = ({
    otpType,
    noOfDigits = 6,
    identifier,
    closeModal,
    onResendOTP,
    isCamsPledgeHigherPriority = false,
    hasKfinNext = false,
    pledgingOrder,
    onCompleteAllOTPs,
    isRefetchNeeded = false,
    assetRepository,
    showLoadingLottie,
    setLoadingLottieKey,
    updatePledgeData,
    pledgeData,
    lottieCams,
    lottieKfin
}: {
    otpType: string,
    isLoading?: boolean,
    noOfDigits?: number,
    identifier?: string,
    closeModal?: () => Promise<void>;
    onResendOTP?: ({otpType, assetRepositoryTemp,
         isRefetchNeededTemp
        }: {otpType: string,
             assetRepositoryTemp: string
              , isRefetchNeededTemp: boolean}) => void,
    isCamsPledgeHigherPriority?: boolean,
    hasKfinNext?: boolean,
    otpChannel?: string,
    pledgingOrder?: any,    
    onCompleteAllOTPs?: () => void,
    isRefetchNeeded?: boolean,
    assetRepository?: string,
    showLoadingLottie?: any,
    setLoadingLottieKey?: (key: string) => void,
    updatePledgeData?: (updatedStepResponseObject?: any) => void,
    pledgeData: any,
    lottieCams: any,
    lottieKfin: any
}) => {

    const userDetails = useAppSelector(state => state.auth.userDetails);
    const dispatch = useAppDispatch();
    const { colors } = useTheme<Theme>();
    const isInitialRender = useRef(true);
    const isMobileBrowser = useIsMobileBrowser()

    const [modalState, setModalState] = useState<'animation' | 'otp' | 'success' | 'error'>('animation')
    const [isVerifying, setIsVerifying] = useState(false);
    const [successLottie, setSuccessLottie] = useState<any>(null);
    const [noOfDigitsLocal, setNoOfDigitsLocal] = useState(noOfDigits);
    const [otpTypeLocal, setOtpTypeLocal] = useState(otpType);
    const [prevOtpTypeLocal, setPrevOtpTypeLocal] = useState(otpType);
    const [pledgingOrderLocal, setPledgingOrderLocal] = useState(pledgingOrder);
    const [isLottieLoading, setIsLottieLoading] = useState(true);
    const [hasError, setHasError] = useState<string>('');
    const [isReportingMode,setIsReportingMode] = useState(false);

    const [isMoreRefetchNeeded, setIsMoreRefetchNeeded] = useState(isRefetchNeeded);
    const [assetRepositoryLocal, setAssetRepositoryLocal] = useState(assetRepository);

    const [identifierLocal, setIdentifierLocal] = useState<string | null>(null);

    const [tempState, setTempState] = useState<{
        approvedLimit: number;
        pendingOTPMap: any;
        isMoreRefetchNeeded: boolean;
    } | null>(pledgeData?.stepResponseObject);

    const [approvedLimit, setApprovedLimit] = useState(0);

   

    const reportOtpIssue = async () => {
        handleOtpReport({
         payload: {
             uniqueId: userDetails?.linkedApplications[0].application_id,
             uniqueIdentifierType: `MF_PLEDGE_PORTFOLIO_${assetRepository}`,
         }
   
     })
     }


    const fetchSuccessLottie = async () => {
        try {
            const response = await fetch("https://lottie.host/34e6bfb8-a91f-4e4f-8856-7571d41cee8e/6sPdPdx4Yf.json", {
                method: "GET",
            })
                .then((response) => response.json())
                .then((responseData) => {
                    setSuccessLottie(responseData);
                })
                .catch((error) => {
                    console.error(error);
                })
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        setIsLottieLoading(true);
        const promises: Promise<any>[] = [fetchSuccessLottie()];
        Promise.all(promises).finally(() => {
            setIsLottieLoading(false);
        })
    }, [])


    useEffect(() => {
        if (!isLottieLoading && isInitialRender.current && modalState === 'animation') {
            isInitialRender.current = false;
            setTimeout(() => {
                setModalState('otp')
            }, 1000)
        }
    }, [isLottieLoading, modalState])

    const handleOtpInput = (otp: string[]) => {
        const OTP = otp.join('');
        if (OTP.length === noOfDigitsLocal) {
            setIsVerifying(true);
            const currentAsset = tempState?.pendingOTPMap ? Object.keys(tempState?.pendingOTPMap).find(key => tempState?.pendingOTPMap[key] === 0) : null;
            const isRefetchNeeded = currentAsset?.includes('_FETCH');
            if (isRefetchNeeded && assetRepositoryLocal && isMoreRefetchNeeded) {
                authCASApi({ applicationId: userDetails?.linkedApplications[0]?.applicationId, otp: OTP, assetRepository: assetRepositoryLocal }).then((response) => {
                    dispatch(updateLinkedApplication(response?.updatedApplicationObj));
                    const otpType = Object.keys(response?.stepResponseObject?.pendingOTPMap).find(key => response?.stepResponseObject?.pendingOTPMap[key] === 0)
                    setModalState('success')                
                    setTempState(response?.stepResponseObject)
                    //Saving the previous otp type before the current otp type is updated
                    setPrevOtpTypeLocal(otpTypeLocal ?? '')
                    setOtpTypeLocal(otpType ?? '')
                    setLoadingLottieKey?.(otpType ?? '')
                    setPledgingOrderLocal(response?.stepResponseObject?.pendingOTPMap)
                    updatePledgeData?.()
                }).catch((err) => {
                    setHasError(err?.data?.message)
                }).finally(() => {
                    setIsVerifying(false);
                })
            } else {
                verifyPledgeOtp({ applicationId: userDetails?.linkedApplications[0]?.applicationId, otpType: otpTypeLocal, otp: OTP }).then((response) => {
                    dispatch(updateLinkedApplication(response?.updatedApplicationObj));
                    setModalState('success')
                    setApprovedLimit(response?.stepResponseObject?.approvedCreditLimit);
                    if (response?.stepResponseObject?.pendingOTPMap) {
                        const otpType = Object.keys(response?.stepResponseObject?.pendingOTPMap).find(key => response?.stepResponseObject?.pendingOTPMap[key] === 0)
                        //Saving the previous otp type before the current otp type is updated
                        setPrevOtpTypeLocal(otpTypeLocal ?? '')
                        setOtpTypeLocal(otpType ?? '')
                        setLoadingLottieKey?.(otpType ?? '')
                        setPledgingOrderLocal(response?.stepResponseObject?.pendingOTPMap)
                    }
                    updatePledgeData?.();
                }).catch((err) => {
                    if(err.status === 400 && err?.data?.voltErrorCode === "MF_OTP_EXPIRED") {
                        setHasError(err?.data?.message);
                    } else {
                        closeModal?.().then(()=> {
                            if(err?.status === 500) {
                                    dispatch(
                                        showErrorSheet({
                                          isErrorSheetVisible: true,
                                          errorMessage: 'Something went wrong. Please try again later.',
                                          closeOnBackdropPress: false,
                                          errorType: 'SERVER_ERROR',
                                          errorHeading: 'Verification failed!',
                                          btnName:'Ok, got it!'
                                        })
                                    )
                            } else {
                                setHasError(err?.data?.message)
                            }
                        });
                    }
                }).finally(() => {
                    setIsVerifying(false);
                })
            }
        }
        else if (OTP.length > 0 && hasError !== '') {
            setHasError('');
        }

    };

    const handleContinue = useCallback(async () => {
        setModalState('animation')
        let assetRepositoryTemp = '';
        let isMoreRefetchNeededTemp = false;
        if (tempState) {
            const existingKeys = Object.keys(tempState?.pendingOTPMap);
            const elementAtZeroPosition = tempState?.pendingOTPMap ? Object.keys(tempState?.pendingOTPMap).find(key => tempState?.pendingOTPMap[key] === 0) : 'No element is at position 0';
            assetRepositoryTemp = elementAtZeroPosition?.includes('CAMS') ? 'CAMS' : 'KARVY';
            setAssetRepositoryLocal(assetRepositoryTemp)
            const fetchKeys = existingKeys.filter(k => k.includes('_FETCH'));
            isMoreRefetchNeededTemp = fetchKeys.length > 0 ? true : false;
            setIsMoreRefetchNeeded(isMoreRefetchNeededTemp)
            setLoadingLottieKey?.(assetRepositoryTemp)
            trackEvent('ASSET_PLEDGE_LIMIT_UNLOCKED_BOTTOMSHEET_CONTINUE_CLICKED', {assetRepository: assetRepositoryTemp});
        }
        if (pledgingOrderLocal && Object.keys(pledgingOrderLocal).length === 0) {
            // go to next screen
            if (onCompleteAllOTPs) {
                onCompleteAllOTPs()
            }
        } else {
            if (isRefetchNeeded && assetRepositoryTemp && isMoreRefetchNeededTemp) {
                // show next fetch modal
                const userCredential = await getCredentialsForAssetFetchApi({ applicationId: userDetails?.linkedApplications[0]?.applicationId, assetRepository: assetRepository })
                fetchInitV2Api({
                    applicationId: userDetails?.linkedApplications[0]?.applicationId,
                    assetRepository: assetRepositoryTemp,
                    emailId: userCredential?.emailId ?? userDetails?.linkedBorrowerAccounts[0]?.accountHolderEmail,
                    panNumber: userCredential?.panNumber ?? userDetails?.linkedBorrowerAccounts[0]?.accountHolderPAN,
                    phoneNumber: userCredential?.phoneNumber ?? userDetails?.linkedBorrowerAccounts[0]?.accountHolderPhoneNumber
                }).then((response) => {
                    // setModalState('animation')
                    setNoOfDigitsLocal(response?.stepResponseObject?.noOfDigits)
                    setIdentifierLocal(response?.stepResponseObject?.identifier)
                    setPledgingOrderLocal(response?.stepResponseObject?.pendingOTPMap)
                    setTimeout(() => setModalState('otp'), 800) // Show animation for 1 second
                    updatePledgeData?.();
                }).catch((err) => {
                    closeModal?.()
                    if (handleApiErr(err)) {
                        dispatch(
                            showErrorSheet({
                                isErrorSheetVisible: true,
                                errorMessage: err?.data?.message,
                                closeOnBackdropPress: true,
                                errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                                ctaLabel: 'Okay'
                            }),
                        );
                    }
                }).finally(() => {
                    setIsVerifying(false);
                })
            } else {
                triggerOtp({ applicationId: userDetails?.linkedApplications[0]?.applicationId, otpType: otpTypeLocal }).then((response) => {
                    // setModalState('animation')
                    setNoOfDigitsLocal(response?.stepResponseObject?.noOfDigits)
                    setIdentifierLocal(response?.stepResponseObject?.identifier)
                    setPledgingOrderLocal(response?.stepResponseObject?.pendingOTPMap)
                    setTimeout(() => setModalState('otp'), 800) // Show animation for 1 second
                }).catch((err) => {
                    updatePledgeData?.();
                    closeModal?.().then(()=> {
                        if(err?.status === 500) {
                                dispatch(
                                    showErrorSheet({
                                      isErrorSheetVisible: true,
                                      errorMessage: 'Something went wrong. Please try again later.',
                                      closeOnBackdropPress: false,
                                      errorType: 'SERVER_ERROR',
                                      errorHeading: 'Verification failed!',
                                      btnName:'Ok, got it!'
                                    })
                                )
                        } else {
                           if (handleApiErr(err)) {
                                dispatch(
                                    showErrorSheet({
                                        isErrorSheetVisible: true,
                                        errorMessage: err?.data?.message,
                                        closeOnBackdropPress: true,
                                        errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                                        ctaLabel: 'Okay'
                                    }),
                                );
                            }
                        }
                    });
                })
            }
        }
    }, [pledgingOrderLocal, otpTypeLocal, isRefetchNeeded, tempState])


    const title = otpTypeLocal && otpTypeLocal.includes('KFIN') ? 'KFintech has sent an OTP' : otpTypeLocal && otpTypeLocal.includes('MFC') ?  'MFC has sent an OTP' : 'CAMS has sent an OTP'
    const currentAsset = pledgeData?.stepResponseObject?.pendingOTPMap ? Object.keys(pledgeData?.stepResponseObject?.pendingOTPMap).find(key => pledgeData?.stepResponseObject?.pendingOTPMap[key] === 0) : null;

    return isReportingMode? (<>
        <ThemedView alignItems='center' padding='lg'>
         <ReportOtp width={100} height={100}/>
         <ThemedText variant='h5s'>Request Received</ThemedText>
       <ThemedText textAlign='center' marginTop='sm' variant='b3r'>
       {"Thanks for the feedback, We are working on the issue, please try again shortly."}    </ThemedText>
   
           <Button handlePress={()=>{
             closeModal?.()
             setTimeout(()=>{
               setIsReportingMode(false)
   
             },1000)
             
           }}
           overRideStyle={{marginTop:24}}
           label='Got it'
           />
         </ThemedView>
     
     </>) : (
        <ThemedView borderTopRightRadius='xl' borderTopLeftRadius='xl' bg='bg-surface-primary' padding='lg' paddingTop='2xl'>
            {modalState === 'success' && (
                <SuccessComponent otpType={otpTypeLocal} handleContinue={handleContinue} approvedLimit={approvedLimit} successLottie={successLottie} prevOtpType={prevOtpTypeLocal} />
            )}


            {modalState === 'animation' && (
                <AnimationComponent showLoadingLottie={showLoadingLottie} otpType={currentAsset} lottieCams={lottieCams} lottieKfin={lottieKfin}/>
            )}
            {modalState === 'otp' && (
                <>
                    <ThemedText variant='h4s' mb='lg' color='text-primary'>{title}</ThemedText>
                    <ThemedView borderWidth={1} borderColor='border-form' mb='lg' alignSelf='stretch' flexDirection='row' alignItems='center' bg='bg-fill-emphasis-secondary' padding='md' borderRadius='md'>
                        <ThemedView flexDirection='row' alignItems='center' justifyContent='center' mr='md' height={40} width={40} bg='bg-fill-primary' borderRadius='full'>
                            <SMSIcon size={18} color={colors['icon-emphasis-secondary']} />
                        </ThemedView>
                        <ThemedView>
                            <ThemedText variant='b4r' color='text-secondary'>OTP was sent to</ThemedText>
                            <ThemedText variant='b3s' color='text-primary'>{identifierLocal ?? identifier}</ThemedText>
                        </ThemedView>
                    </ThemedView>
                    <OTPVerification
                        noOfDigits={noOfDigitsLocal}
                        hasError={hasError}
                        handleOtpInput={handleOtpInput}
                        resendTimerCount={30}
                        triggerResendOTP={onResendOTP ? () => onResendOTP({otpType: otpTypeLocal, assetRepositoryTemp: assetRepositoryLocal!, isRefetchNeededTemp: isMoreRefetchNeeded}) : () => { }}
                        verifying={isVerifying}
                        blockResend={false}
                        isDarkMode={false}
                        onCustomerFacingIssue={()=>{
                            setIsReportingMode(true);
                            reportOtpIssue()
             

                        }}
                    />
                    {isMobileBrowser && <ThemedView height={30} />}
                    {Platform.OS === 'android' && <ThemedView height={30} />}
                </>
            )}
        </ThemedView>
    )
}
export default React.memo(KarvyPledgeOtpModal);
